import {
  isEditorToRendererMessageType,
  type EditorToRendererMessage,
  type EditorToRendererMessageType,
  type RendererToEditorMessage,
} from "@lureo/editor-contracts";
import { filter, Subject, type Observable } from "rxjs";
import { EditorReadAfter } from "./EditorReadAfter";
import { EditorReadHomeUntil } from "./EditorReadHomeUntil";
import type { BridgeContractCheck, BridgeLogger, EditorMessage } from "./RendererBridgePort";

// Parsed, then dropped without a word: the UI element messages have no live handler, the other three are the template's.
const NO_RENDERER_HANDLER: ReadonlySet<unknown> = new Set([
  "uiElementInstanceAdded",
  "uiElementInstanceUpdated",
  "uiElementInstanceRemoved",
  "setJwt",
  "checkCanvasReady",
  "requestScreenshot",
]);

/** The generated C# DTO's name, which every RendererBridge log line names the message by. */
export const messageDtoName = (type: string): string => `${type.charAt(0).toUpperCase()}${type.slice(1)}Message`;

function parse(json: string): EditorToRendererMessage | null {
  const token: unknown = JSON.parse(json);
  if (typeof token !== "object" || token === null || Array.isArray(token)) {
    throw new Error("Error reading JObject from JsonReader");
  }
  const type = (token as Readonly<Record<string, unknown>>)["type"];
  if (NO_RENDERER_HANDLER.has(type)) return null;
  if (!isEditorToRendererMessageType(type)) {
    const shown = type === undefined || type === null ? "" : typeof type === "string" ? type : JSON.stringify(type);
    throw new Error(`Unknown message type '${shown}'`);
  }
  if (type === "readAfter") return EditorReadAfter.parse(token as Readonly<Record<string, unknown>>);
  if (type === "readHomeUntil") return EditorReadHomeUntil.parse(token as Readonly<Record<string, unknown>>);
  return token as EditorToRendererMessage;
}

const reasonOf = (error: unknown): string => (error instanceof Error ? error.message : String(error));

/** What every link shares: the message rules of Unity's RendererBridge component, whatever carries the JSON. */
export class RendererBridgeCore {
  readonly #received = new Subject<EditorToRendererMessage>();
  readonly received$: Observable<EditorToRendererMessage> = this.#received.asObservable();

  constructor(
    private readonly logger: BridgeLogger,
    private readonly contractCheck: BridgeContractCheck | undefined,
  ) {}

  on<T extends EditorToRendererMessageType>(type: T): Observable<EditorMessage<T>> {
    return this.received$.pipe(filter((message): message is EditorMessage<T> => message.type === type));
  }

  receive(json: string): void {
    let message: EditorToRendererMessage | null;
    try {
      message = parse(json);
    } catch (error) {
      this.logger.warn(`Dropping malformed message: ${reasonOf(error)}`, json);
      return;
    }
    if (message === null) return;
    this.#reportProblems(message.type, this.contractCheck?.editorMessageProblems(message), json);
    this.logger.log(`Received ${messageDtoName(message.type)}`, json);
    this.#received.next(message);
  }

  /** The JSON the editor is sent, logged as Send logs it. */
  serialize(message: RendererToEditorMessage): string {
    const json = JSON.stringify(message);
    this.logger.log(`Sending ${messageDtoName(message.type)}`, json);
    this.#reportProblems(message.type, this.contractCheck?.rendererMessageProblems(message), json);
    return json;
  }

  complete(): void {
    this.#received.complete();
  }

  #reportProblems(type: string, problems: readonly string[] | undefined, json: string): void {
    if (problems === undefined || problems.length === 0) return;
    this.logger.warn(`${messageDtoName(type)} breaks the contract: ${problems.join("; ")}`, json);
  }
}
